/**
 * Language utilities for displaying language names and handling RTL text.
 * 
 * This module provides utilities for working with language codes,
 * displaying language names, and handling right-to-left text rendering.
 */

/**
 * Mapping of ISO 639-1 language codes to display names
 */
export const LANGUAGE_NAMES = {
  'en': 'English',
  'zh': '中文',
  'es': 'Español', 
  'fr': 'Français',
  'de': 'Deutsch',
  'ja': '日本語',
  'ko': '한국어',
  'pt': 'Português',
  'it': 'Italiano',
  'ru': 'Русский',
  'ar': 'العربية',
  'he': 'עברית',
  'hi': 'हिन्दी',
  'th': 'ไทย',
  'tr': 'Türkçe',
  'pl': 'Polski',
  'nl': 'Nederlands',
  'sv': 'Svenska',
  'da': 'Dansk',
  'no': 'Norsk',
  'fi': 'Suomi',
  'cs': 'Čeština',
  'sk': 'Slovenčina',
  'hu': 'Magyar',
  'ro': 'Română',
  'bg': 'Български',
  'hr': 'Hrvatski',
  'sr': 'Српски',
  'sl': 'Slovenščina',
  'et': 'Eesti',
  'lv': 'Latviešu',
  'lt': 'Lietuvių',
  'uk': 'Українська',
  'el': 'Ελληνικά',
  'mk': 'Македонски',
  'mt': 'Malti',
  'ca': 'Català',
  'eu': 'Euskera',
  'gl': 'Galego',
  'cy': 'Cymraeg',
  'ga': 'Gaeilge',
  'is': 'Íslenska',
  'fo': 'Føroyskt',
  'sq': 'Shqip',
  'bs': 'Bosanski',
  'me': 'Crnogorski'
};

/**
 * Languages that use right-to-left text direction
 */
export const RTL_LANGUAGES = new Set(['ar', 'he', 'fa', 'ur', 'yi', 'ji', 'iw', 'ku', 'ps']);

/**
 * Get the display name for a language code
 * @param {string} languageCode - ISO 639-1 language code
 * @returns {string} Human-readable language name
 */
export function getLanguageName(languageCode) {
  if (!languageCode) return '';
  
  const normalizedCode = languageCode.toLowerCase();
  return LANGUAGE_NAMES[normalizedCode] || languageCode.toUpperCase();
}

/**
 * Check if a language uses right-to-left text direction
 * @param {string} languageCode - ISO 639-1 language code
 * @returns {boolean} True if language is RTL
 */
export function isRTL(languageCode) {
  if (!languageCode) return false;
  
  const normalizedCode = languageCode.toLowerCase();
  return RTL_LANGUAGES.has(normalizedCode);
}

/**
 * Get CSS classes for language badge styling
 * @param {string} languageCode - ISO 639-1 language code
 * @returns {string} CSS classes for styling the language badge
 */
export function getLanguageBadgeClasses(languageCode) {
  const baseClasses = 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium';
  
  if (!languageCode || languageCode === 'en') {
    return `${baseClasses} bg-gray-100 text-gray-800`;
  }
  
  // Different colors for different language families
  const colorMap = {
    // Romance languages - blue
    'es': 'bg-blue-100 text-blue-800',
    'fr': 'bg-blue-100 text-blue-800', 
    'it': 'bg-blue-100 text-blue-800',
    'pt': 'bg-blue-100 text-blue-800',
    'ro': 'bg-blue-100 text-blue-800',
    'ca': 'bg-blue-100 text-blue-800',
    
    // Germanic languages - green
    'de': 'bg-green-100 text-green-800',
    'nl': 'bg-green-100 text-green-800',
    'sv': 'bg-green-100 text-green-800',
    'da': 'bg-green-100 text-green-800',
    'no': 'bg-green-100 text-green-800',
    
    // Slavic languages - purple
    'ru': 'bg-purple-100 text-purple-800',
    'pl': 'bg-purple-100 text-purple-800',
    'cs': 'bg-purple-100 text-purple-800',
    'sk': 'bg-purple-100 text-purple-800',
    'uk': 'bg-purple-100 text-purple-800',
    'bg': 'bg-purple-100 text-purple-800',
    'hr': 'bg-purple-100 text-purple-800',
    'sr': 'bg-purple-100 text-purple-800',
    'sl': 'bg-purple-100 text-purple-800',
    
    // East Asian languages - red
    'zh': 'bg-red-100 text-red-800',
    'ja': 'bg-red-100 text-red-800',
    'ko': 'bg-red-100 text-red-800',
    
    // Semitic languages - yellow
    'ar': 'bg-yellow-100 text-yellow-800',
    'he': 'bg-yellow-100 text-yellow-800',
    
    // Other languages - indigo
    'hi': 'bg-indigo-100 text-indigo-800',
    'th': 'bg-indigo-100 text-indigo-800',
    'tr': 'bg-indigo-100 text-indigo-800',
    'fi': 'bg-indigo-100 text-indigo-800',
    'hu': 'bg-indigo-100 text-indigo-800',
    'el': 'bg-indigo-100 text-indigo-800'
  };
  
  const colorClass = colorMap[languageCode.toLowerCase()] || 'bg-gray-100 text-gray-800';
  return `${baseClasses} ${colorClass}`;
}

/**
 * Get text direction for a language
 * @param {string} languageCode - ISO 639-1 language code
 * @returns {string} 'rtl' or 'ltr'
 */
export function getTextDirection(languageCode) {
  return isRTL(languageCode) ? 'rtl' : 'ltr';
}

/**
 * Get appropriate margin classes for RTL/LTR support
 * @param {string} languageCode - ISO 639-1 language code
 * @returns {object} Object with logical property classes
 */
export function getDirectionalClasses(languageCode) {
  const rtl = isRTL(languageCode);
  
  return {
    // Use Tailwind's logical properties for better RTL support
    marginStart: rtl ? 'mr-2' : 'ml-2',
    marginEnd: rtl ? 'ml-2' : 'mr-2',
    paddingStart: rtl ? 'pr-3' : 'pl-3', 
    paddingEnd: rtl ? 'pl-3' : 'pr-3',
    textAlign: rtl ? 'text-right' : 'text-left',
    borderStart: rtl ? 'border-r' : 'border-l',
    borderEnd: rtl ? 'border-l' : 'border-r'
  };
}

/**
 * Check if we should show a language badge (don't show for English to reduce noise)
 * @param {string} languageCode - ISO 639-1 language code
 * @returns {boolean} True if badge should be shown
 */
export function shouldShowLanguageBadge(languageCode) {
  return languageCode && languageCode.toLowerCase() !== 'en';
}

/**
 * Format language code for display in a compact format
 * @param {string} languageCode - ISO 639-1 language code
 * @returns {string} Formatted language code (uppercase)
 */
export function formatLanguageCode(languageCode) {
  if (!languageCode) return '';
  return languageCode.toUpperCase();
}